import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LoginModal = ({ open, onClose }) => {
  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const { signIn, signUp, confirmSignUp, isLoading } = useAuth();

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    
    // Debug: Log email state
    console.log('Sign in attempt - email:', email, 'password length:', password?.length);
    
    if (!email || !email.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    const result = await signIn(email.trim(), password);
    
    if (result.success) {
      onClose();
    } else {
      // Check if this is an unconfirmed account error
      if (result.needsConfirmation || result.error.includes('confirm your email') || result.error.includes('verification code')) {
        setError(result.error);
        // Switch to confirmation mode
        setSuccess('Please enter the verification code sent to your email.');
        setMode('confirm');
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    const result = await signUp(email, password, name);
    
    if (result.success) {
      setSuccess('Account created! Please check your email for a verification code.');
      // Don't clear email when switching to confirm mode - we need it for confirmation
      setMode('confirm');
    } else {
      setError(result.error || 'Sign up failed. Please try again.');
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setError('');
    
    // Debug: Log current state
    console.log('Confirm sign up attempt - email:', email, 'verificationCode:', verificationCode);
    
    const result = await confirmSignUp(email, verificationCode);
    
    if (result.success) {
      setSuccess('Account confirmed! You can now sign in.');
      setMode('signin');
    } else {
      setError(result.error || 'Confirmation failed. Please try again.');
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    
    // Re-sign up to trigger a new verification code
    const result = await signUp(email, 'tempPassword123', 'tempName');
    
    if (result.success) {
      setSuccess('A new verification code has been sent to your email.');
    } else {
      setError(result.error || 'Failed to resend verification code.');
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setEmail('');
    setPassword('');
    setName('');
    setConfirmPassword('');
    setVerificationCode('');
    setMode('signin');
    onClose();
  };

  const handleTabChange = (event, newMode) => {
    setError('');
    setSuccess('');
    // Don't clear email when switching between signin/signup/confirm modes
    // Only clear email when closing the modal or switching from confirm to other modes
    if (newMode === 'signin' || newMode === 'signup' || newMode === 'confirm') {
      // Keep email for all auth modes
    } else {
      setEmail('');
    }
    setPassword('');
    setName('');
    setConfirmPassword('');
    setVerificationCode('');
    setMode(newMode);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          {mode === 'confirm' ? 'Confirm Email' : mode === 'signup' ? 'Sign Up for Bloomaroot' : 'Sign In to Bloomaroot'}
        </Typography>
        <Button onClick={handleClose} size="small">
          <Close />
        </Button>
      </DialogTitle>
      
      {mode !== 'confirm' && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={mode} onChange={handleTabChange}>
            <Tab label="Sign In" value="signin" />
            <Tab label="Sign Up" value="signup" />
          </Tabs>
        </Box>
      )}
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Avatar
            sx={{ width: 64, height: 64, mb: 2, bgcolor: '#89479a' }}
          />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {mode === 'confirm' 
              ? 'Enter the verification code sent to your email'
              : mode === 'signup' 
              ? 'Create your account to access personalized tools'
              : 'Access your personalized tools and features'
            }
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {mode === 'confirm' ? (
          <Box component="form" onSubmit={handleConfirmSignUp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
              fullWidth
              disabled={isLoading}
              placeholder="Enter 6-digit code"
            />
            <Button 
              onClick={handleResendCode} 
              disabled={isLoading}
              sx={{ alignSelf: 'flex-start' }}
            >
              Resend Code
            </Button>
          </Box>
        ) : mode === 'signup' ? (
          <Box component="form" onSubmit={handleSignUp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              disabled={isLoading}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={isLoading}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              disabled={isLoading}
              helperText="Minimum 8 characters"
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
              disabled={isLoading}
            />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={isLoading}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              disabled={isLoading}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={mode === 'confirm' ? handleConfirmSignUp : mode === 'signup' ? handleSignUp : handleSignIn}
          variant="contained"
          disabled={isLoading || (mode === 'signin' ? !email || !password : mode === 'signup' ? !email || !password || !name || !confirmPassword : !verificationCode)}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading 
            ? (mode === 'confirm' ? 'Confirming...' : mode === 'signup' ? 'Creating Account...' : 'Signing In...')
            : (mode === 'confirm' ? 'Confirm Email' : mode === 'signup' ? 'Create Account' : 'Sign In')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginModal;
